import type { WidgetConfig, WidgetData, WidgetMetrics } from '../types';
import type { MetricColorKey } from '../components/PlumPrint';

export interface CategoryConfig {
  label: string;
  metricsKey: MetricColorKey;
  statsKey: keyof WidgetData['statistics'];
  hideFlag: keyof WidgetConfig;
  /**
   * How same-label entries from different sources are combined into one displayed
   * total in the compact panels (`MetricsPanel`/`Summary`): `'max'` avoids
   * double-counting the same underlying record when multiple citation indexes
   * report overlapping data; `'sum'` adds genuinely distinct per-source counts
   * together. Citations is the only category using `'max'`.
   */
  aggregation: 'max' | 'sum';
  /**
   * How the `Details` view renders this category's stat entries, per legacy
   * `details.coffee`/`statistics.coffee`: `'breakdown'` shows every per-source
   * entry as its own row (Citations/Captures/Usage — the source, e.g. an index
   * or repository name, is the meaningful distinction); `'aggregate'` combines
   * same-label entries via `groupByLabel` into one row per label (Mentions/Social
   * Media — many entries share one source-derived label, e.g. many "News
   * Mentions" URLs). Unlike `aggregation`, this only affects `Details.tsx`.
   */
  detailsMode: 'breakdown' | 'aggregate';
}

// Order matches legacy `ORDER` in details.coffee/category.coffee/counts.coffee.
export const CATEGORIES: CategoryConfig[] = [
  {
    label: 'Citations',
    metricsKey: 'citation',
    statsKey: 'Citations',
    hideFlag: 'hideCitations',
    aggregation: 'max',
    detailsMode: 'breakdown',
  },
  {
    label: 'Usage',
    metricsKey: 'usage',
    statsKey: 'Usage',
    hideFlag: 'hideUsage',
    aggregation: 'sum',
    detailsMode: 'breakdown',
  },
  {
    label: 'Captures',
    metricsKey: 'capture',
    statsKey: 'Captures',
    hideFlag: 'hideCaptures',
    aggregation: 'sum',
    detailsMode: 'breakdown',
  },
  {
    label: 'Mentions',
    metricsKey: 'mention',
    statsKey: 'Mentions',
    hideFlag: 'hideMentions',
    aggregation: 'sum',
    detailsMode: 'aggregate',
  },
  {
    label: 'Social Media',
    metricsKey: 'socialMedia',
    statsKey: 'Social Media',
    hideFlag: 'hideSocialMedia',
    aggregation: 'sum',
    detailsMode: 'aggregate',
  },
];

/** Returns only categories with data that aren't hidden */
export const visibleCategories = (
  categories: CategoryConfig[],
  metrics: WidgetMetrics,
  config: WidgetConfig,
) => categories.filter(({ metricsKey, hideFlag }) => !config[hideFlag] && metrics[metricsKey] > 0);

/**
 * True when at least one category remains after applying the hide flags. Drives the
 * widget's empty state so filters that hide every available category render nothing
 * (RDPNEXUS-836 FR-3.1 / AR-3.1) — the single emptiness signal shared with the panels.
 */
export const hasVisibleMetrics = (metrics: WidgetMetrics, config: WidgetConfig): boolean =>
  visibleCategories(CATEGORIES, metrics, config).length > 0;
